import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { serverAPI } from '../utils/api';
import Rating from './Rating';
import ServerStatus from './ServerStatus';
import CategoryRating from './CategoryRating';
import { useLanguage } from '../contexts/LanguageContext';

interface ServerCardProps {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  rating: number;
  status: string;
  initialIsFavorite?: boolean;
  banner?: string;
  onlineRating?: number;
  managementRating?: number;
  gameplayRating?: number;
}

const ServerCard: React.FC<ServerCardProps> = ({
  id,
  name,
  description,
  createdAt,
  rating,
  status,
  initialIsFavorite = false,
  banner,
  onlineRating = 0,
  managementRating = 0,
  gameplayRating = 0,
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
  const [bannerError, setBannerError] = useState(false);

  const launchDateLabel = useMemo(() => {
    const date = new Date(createdAt);
    return date.toLocaleDateString();
  }, [createdAt]);

  const handleDetailsClick = () => {
    navigate(`/server/${id}`);
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await serverAPI.toggleFavorite(id);
      setIsFavorite(result.favorited);
    } catch (error) {
      console.error('Failed to toggle favorite', error);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 via-blue-100 to-sky-100 dark:bg-gradient-to-br dark:from-gray-800 dark:to-gray-900 rounded-xl overflow-hidden shadow-lg dark:shadow-2xl hover:shadow-xl dark:hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-blue-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-gray-600">
      {/* Banner Image - Made larger to be more prominent */}
      {banner && !bannerError ? (
        <div className="h-40 md:h-56 w-full overflow-hidden relative bg-gray-200 dark:bg-gray-700">
          <img
            src={banner}
            alt={`${name} banner`}
            className="w-full h-full object-cover transition-opacity duration-300"
            referrerPolicy="no-referrer"
            loading="lazy"
            decoding="async"
            onError={() => setBannerError(true)}
          />
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

          {/* Info overlay on image */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ServerStatus status={status as any} />
                <span className="text-white/90 text-sm flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {launchDateLabel}
                </span>
              </div>
              <Rating rating={rating} compact />
            </div>
          </div>
        </div>
      ) : (
        <div className="h-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 dark:from-blue-900 dark:to-purple-900" />
      )}
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white truncate flex-1 mr-2">{name}</h3>
          <button
            onClick={handleFavoriteClick}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${isFavorite
              ? 'bg-green-500/20 text-green-600 dark:text-green-400 hover:bg-green-500/30'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
          >
            {isFavorite ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {t('server.card.in_library') || 'Kütüphanede'}
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t('server.card.add_to_library') || 'Kütüphaneye Ekle'}
              </>
            )}
          </button>
        </div>

        {/* Short description display */}
        {description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
            {description}
          </p>
        )}

        {/* If no banner, show info here */}
        {(!banner || bannerError) && (
          <div className="flex items-center gap-3 mb-3 text-sm text-gray-500 dark:text-gray-400">
            <ServerStatus status={status as any} />
            <span className="flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {launchDateLabel}
            </span>
            <Rating rating={rating} compact />
          </div>
        )}

        {/* Category Ratings */}
        {(onlineRating > 0 || managementRating > 0 || gameplayRating > 0) && (
          <div className="flex flex-col gap-1 mb-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <CategoryRating category="online" rating={onlineRating} size="sm" />
            <CategoryRating category="management" rating={managementRating} size="sm" />
            <CategoryRating category="gameplay" rating={gameplayRating} size="sm" />
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleDetailsClick}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-5 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 text-sm"
          >
            {t('common.view_details')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServerCard;